import { useCallback, useMemo, useState } from 'react';

import {
  type NumberStateOptions,
  useNumberState,
} from '@client/hooks/use-number-state.hooks';

type SliderStateOptions = {
  /** External value used for sync. */
  value: number;
  /** Commit handler. */
  onCommit: (value?: number) => void;
  /** Options forwarded to {@link useNumberState}. */
  options?: Omit<NumberStateOptions, 'isSyncBlocked'>;
};

type SliderState = {
  /** Current slider value. */
  value: number;
  /** Update slider value from code (not tied to drag events). */
  setValue: (value: number) => void;
  /** True while pointer drag is active. */
  isDragging: boolean;
  /** Slider onChange handler for live updates while dragging. */
  onChange: (event: Event | React.SyntheticEvent, value: number) => void;
  /** Slider onChangeCommitted handler for persistence. */
  onChangeCommitted: (
    event: Event | React.SyntheticEvent,
    value: number,
  ) => void;
};

/**
 * Manage slider state with drag tracking and commit semantics.
 *
 * Usage:
 * - Use {@link SliderState.onChange} to update UI while dragging.
 * - Use {@link SliderState.onChangeCommitted} to persist the value.
 * - Use {@link SliderState.setValue} for programmatic updates.
 * - Use {@link SliderState.isDragging} to gate UI that should not sync mid-drag.
 */
export const useSliderState = ({
  value,
  onCommit,
  options,
}: SliderStateOptions): SliderState => {
  const [isDragging, setIsDragging] = useState(false);
  const [state, setState] = useNumberState(value, {
    ...options,
    isSyncBlocked: isDragging,
  });
  const handleChange = useCallback<SliderState['onChange']>(
    (_event, nextValue) => {
      setIsDragging(true);
      setState(nextValue);
    },
    [setState],
  );
  const handleCommit = useCallback<SliderState['onChangeCommitted']>(
    (_event, nextValue) => {
      setIsDragging(false);
      onCommit(nextValue);
    },
    [onCommit],
  );

  return useMemo(
    () => ({
      value: state,
      setValue: setState,
      isDragging,
      onChange: handleChange,
      onChangeCommitted: handleCommit,
    }),
    [handleChange, handleCommit, isDragging, state, setState],
  );
};
